import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import ModalTemplate from '../../../../uiKit/ModalTemplate'
import classes from '../../../widget/components/AddGreetingModal/styles.module.scss'
import inviteModalClasses from './styles.module.scss'
import { OneUserTab } from './OneUserTab'
import { BulkInviteUploadTab } from '../BulkInviteUploadTab/BulkInviteUploadTab'
import { emailValidation } from '../../../../helpers/emailValidation'
import { getUsers, inviteNewUser, importBulkInviteUsers, showDuplicates, validateBulkInviteFile, uploadFile as uploadFileApi } from '../../api/usersApi'
import { UploadingStatuses } from '../../helpers/constants'
import { countInviteCsvDataRows, isBulkInviteValidationResponse } from '../../helpers/bulkInviteValidationHelpers'
import { BulkInviteValidationResponse } from '../../model/bulkInviteTypes'
import { BulkInviteValidationModal } from '../BulkInviteValidationModal/BulkInviteValidationModal'
import { BulkInviteImportSuccessModal } from '../BulkInviteImportSuccessModal/BulkInviteImportSuccessModal'
// @ts-ignore
import Template from '../../helpers/Template.csv'

interface Props {
  botId: string
  isOpenUserModal: boolean
  onClose: () => void
  setDuplicateAttentionModal: Dispatch<SetStateAction<{ isOpen: boolean; duplicateUsers: string }>>
  refreshUsersFromStart: () => Promise<unknown>
}

type TabType = 'One user' | 'Multiple users'
const tabs: TabType[] = ['One user', 'Multiple users']
const BRAND_BLUE = '#00A0E3'
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024
const MAX_USER_ROWS = 1000

export type FormType = {
  first_name: string
  last_name: string
  email: string
  role: string
  manager_email?: string
}

export type ErrorsType = {
  first_name: string
  last_name: string
  email: string
  manager_email?: string
}

export const InviteUserModal: FC<Props> = ({
  botId,
  isOpenUserModal,
  onClose,
  setDuplicateAttentionModal,
  refreshUsersFromStart
}) => {
  const [selectedTab, setSelectedTab] = useState(tabs[0])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadingStatus, setUploadingStatus] = useState(UploadingStatuses.PENDING)
  const [error, setError] = useState('')
  const [fileName, setFileName] = useState('')
  const [validationResult, setValidationResult] = useState<BulkInviteValidationResponse | null>(null)
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false)
  const [isImportSuccessModalOpen, setIsImportSuccessModalOpen] = useState(false)
  const [successfullyImportedCount, setSuccessfullyImportedCount] = useState(0)
  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    manager_email: '',
  })
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'CARER',
    manager_email: null,
  })
  const modalTitle = useMemo(() => {
    return 'Invite to CareBrain App'
  }, [selectedTab])

  const uploadResponseHandler = (response, data) => {
    if (response.status === 200 && isBulkInviteValidationResponse(data)) {
      setError('')
      setValidationResult(data)
      setIsValidationModalOpen(true)
      return
    }

    if (response.status === 200) {
      setError('')
      setUploadingStatus(UploadingStatuses.SUCCESS)
      refreshUsersFromStart()
    } else if (response.status === 400) {
      setUploadingStatus(UploadingStatuses.ERROR)
      setError(data.message || '')
    } else if (response.status === 500) {
      setError('')
      setUploadingStatus(UploadingStatuses.ERROR)
    }
  }

  const resetUploadState = () => {
    setUploadingStatus(UploadingStatuses.PENDING)
    setError('')
    setFileName('')
    setValidationResult(null)
    setIsValidationModalOpen(false)
    setIsImportSuccessModalOpen(false)
    setSuccessfullyImportedCount(0)
  }

  const handleUploadDifferentFile = () => {
    resetUploadState()
  }

  const handleValidationModalClose = () => {
    resetUploadState()
    onClose()
  }

  const handleImportValidRecords = async () => {
    if (!validationResult?.validUsers.length || validationResult.validRecords <= 0) {
      return
    }

    const importResult = await importBulkInviteUsers(botId, validationResult.validUsers)

    if (importResult.failed > 0) {
      return
    }

    await getUsers(botId, 0)

    // Open success before closing validation so the upload modal does not briefly reopen (React 17).
    setSuccessfullyImportedCount(importResult.successfullyImported)
    setIsImportSuccessModalOpen(true)
    setIsValidationModalOpen(false)
  }

  const handleImportSuccessClose = async () => {
    // Close parent + success immediately so the upload modal never reappears.
    setIsImportSuccessModalOpen(false)
    resetUploadState()
    onClose()

    const duplicates = await showDuplicates(botId)
    if (duplicates?.length) {
      setDuplicateAttentionModal({
        isOpen: true,
        duplicateUsers: duplicates.join(', '),
      })
    }
  }

  const handleUpload = async event => {
    const file = event.target.files[0]

    if (!file) {
      return
    }

    setFileName(file.name)

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadingStatus(UploadingStatuses.ERROR)
      setError('File format not supported. Please upload a CSV file.')
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadingStatus(UploadingStatuses.ERROR)
      setError('File size exceeds the maximum allowed limit of 15MB.')
      return
    }

    setIsUploading(true)
    setError('')

    try {
      const fileText = await file.text()
      const rowCount = countInviteCsvDataRows(fileText)

      if (rowCount > MAX_USER_ROWS) {
        setUploadingStatus(UploadingStatuses.ERROR)
        setError(`File contains ${rowCount} records, which exceeds the maximum allowed limit of ${MAX_USER_ROWS} records.`)
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      const response = await validateBulkInviteFile(botId, formData)
      const data = await response.json()
      uploadResponseHandler(response, data)
    } catch {
      setUploadingStatus(UploadingStatuses.ERROR)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSave = () => {
    if (!form.first_name || form.first_name.trim() == '') {
      setErrors(prevState => ({
        ...prevState,
        first_name: 'First Name can’t be empty',
      }))
    }
    if (!form.last_name || form.last_name.trim() == '') {
      setErrors(prevState => ({
        ...prevState,
        last_name: 'Last Name can’t be empty',
      }))
    }
    if (!emailValidation(form.email)) {
      setErrors(prevState => ({
        ...prevState,
        email: 'Incorrect email format',
      }))
    }

    // Check if any required fields are invalid
    const isFirstNameValid = form.first_name && form.first_name.trim() !== ''
    const isLastNameValid = form.last_name && form.last_name.trim() !== ''
    const isEmailValid = emailValidation(form.email)

    if (!isFirstNameValid || !isLastNameValid || !isEmailValid) {
      return
    }

    inviteNewUser({ ...form, botId }).then(() => {
      refreshUsersFromStart().then(() => {
        showDuplicates(botId).then(res => {
          if (res.length) {
            setDuplicateAttentionModal({
              isOpen: true,
              duplicateUsers: res.join(', '),
            })
          }
        })
      })
    })

    onClose()
  }

  const isUploadModalOpen = isOpenUserModal && !isValidationModalOpen && !isImportSuccessModalOpen

  return (
    <>
      {isUploadModalOpen && (
        <ModalTemplate
          title={modalTitle}
          open
          onClose={onClose}
          onSave={handleSave}
          primaryButtonTitle="Invite"
          hasPrimaryButton={selectedTab === tabs[0]}
          containerClasses={inviteModalClasses.modalContainer}
          bodyClasses={inviteModalClasses.modalTemplateBody}
          cancelButtonStyles={selectedTab === tabs[1] ? {
            borderRadius: 8,
            paddingLeft: 12,
            paddingRight: 12,
            color: BRAND_BLUE,
            borderColor: BRAND_BLUE,
          } : undefined}
          disableClose={isUploading}
        >
          <>
            <div className={inviteModalClasses.modalContent}>
              <div className={`${classes.tabs} ${inviteModalClasses.tabsRow}`}>
                {tabs.map((tab, index) => (
                  <div
                    key={index}
                    className={selectedTab === tab ? classes.activeTab : classes.tab}
                    onClick={() => {
                      setSelectedTab(tab)
                      setError('')
                      setUploadingStatus(UploadingStatuses.PENDING)
                    }}>
                    {tab}
                  </div>
                ))}
              </div>
              <div className={inviteModalClasses.modalBody} id="scrollable">
                {selectedTab === tabs[0] && (
                  <OneUserTab form={form} setForm={setForm} errors={errors} setErrors={setErrors} botId={botId} />
                )}
                {selectedTab === tabs[1] && (
                  <BulkInviteUploadTab
                    onUpload={handleUpload}
                    isUploading={isUploading}
                    uploadingStatus={uploadingStatus}
                    errorText={error}
                    template={Template}
                  />
                )}
              </div>
            </div>
          </>
        </ModalTemplate>
      )}
      {validationResult && (
        <BulkInviteValidationModal
          open={isValidationModalOpen}
          botId={botId}
          fileName={fileName}
          validationResult={validationResult}
          onClose={handleValidationModalClose}
          onUploadDifferentFile={handleUploadDifferentFile}
          onImportValidRecords={handleImportValidRecords}
        />
      )}
      <BulkInviteImportSuccessModal
        open={isImportSuccessModalOpen}
        successfullyImported={successfullyImportedCount}
        onClose={handleImportSuccessClose}
      />
    </>
  )
}
